import React, { useState, useLayoutEffect, useEffect } from "react";
import {
  StyleSheet,
  FlatList,
  ActivityIndicator,
  View,
  TouchableOpacity,
} from "react-native";
import { colors } from "src/assets";
import { SearchBar, SectionHeader, NotFound } from "./components";
import {
  Button,
  OfferListItem,
  OfferListItemLarge,
  Typography,
} from "src/components";
import { useNavigation } from "@react-navigation/native";
import { useRecommendations, useSearch } from "./api";
import { useHomeFilter } from "src/hooks/home-filter";
import { tabChangeEmit } from "src/utils/event";
import { IServiceFilters, ServiceOrder } from "src/domains";
import LocalText from "src/local";
import { MainTabScreenProps } from "src/navigation/types";
import { useAuth, AuthState } from "src/hooks/cognito";

enum Layout {
  Recommendation,
  Search,
}

export const HomeScreen: React.FC = () => {
  const auth = useAuth();
  const [showSearchBar, setShowSearcBar] = useState<boolean>(true);
  const [itemsR, totalCountR, , , , nextPageR] = useRecommendations();
  const [searchFilter, setSearchFilter] = useState<
    IServiceFilters | undefined
  >();
  const [searchOrder, setSearchOrder] = useState<ServiceOrder>(
    ServiceOrder.SERVICE_RECOMMENDATION
  );
  const [itemsS, totalCountS, loadingS, , , nextPageS] = useSearch(
    searchOrder,
    searchFilter
  );
  const [layout, setLayout] = useState<Layout>(Layout.Recommendation);
  const [searchText, setSearchText] = useState<string>("");
  const [tempSearchText, setTempSearchText] = useState<string>("");
  const [filter, isFilterSetted] = useHomeFilter();
  const navigation =
    useNavigation<MainTabScreenProps<"Search">["navigation"]>();
  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <Button
          onPress={() => setShowSearcBar((v) => !v)}
          variant={"icon"}
          icon={"search"}
          iconSize={24}
          iconColor={"black"}
          containerStyle={styles.headerButtonLeft}
        />
      ),
      headerRight: () =>
        auth === AuthState.Auth ? (
          <Button
            onPress={() => navigation.push("HomeFilter")}
            variant={"icon"}
            icon={"settings"}
            iconSize={24}
            iconColor={"black"}
            containerStyle={styles.headerButtonRight}
          />
        ) : (
          <TouchableOpacity
            style={styles.headerButtonRight}
            onPress={() => navigation.navigate("Auth", { redirect: undefined })}
          >
            <Typography
              text="ログイン＆新規登録"
              textStyle={{ color: colors.chokuRed }}
            />
          </TouchableOpacity>
        ),
    });
  }, [navigation, auth]);
  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", (e) => {
      tabChangeEmit("Home");
    });
    return () => {
      unsubscribe();
    };
  }, [navigation]);
  useEffect(() => {
    if (isFilterSetted || searchText.length > 0) {
      setSearchFilter({
        ...filter.filter,
        searchString: searchText.length > 0 ? searchText : undefined,
      });
      if (layout !== Layout.Search) {
        setLayout(Layout.Search);
      }
    } else {
      if (layout !== Layout.Recommendation) {
        setSearchFilter(undefined);
        setLayout(Layout.Recommendation);
      }
    }
    setSearchOrder(filter.order);
  }, [filter, isFilterSetted, searchText]);
  const offerHandler = (offerId: string) => {
    navigation.navigate("Offer", { offerId });
  };
  return (
    <View
      style={[
        styles.container,
        layout === Layout.Search && !loadingS && itemsS.length === 0
          ? styles.notFound
          : {},
      ]}
    >
      {showSearchBar ? (
        <SearchBar
          onClose={() => {
            setShowSearcBar(false);
            setLayout(Layout.Recommendation);
            setSearchText("");
          }}
          value={tempSearchText}
          onChangeText={(text: string) => setTempSearchText(text)}
          onBlur={() => {
            if (tempSearchText.length === 0) {
              setSearchText(tempSearchText);
              setLayout(Layout.Recommendation);
            } else {
              setSearchText(tempSearchText);
              setLayout(Layout.Search);
            }
          }}
        />
      ) : null}
      {layout === Layout.Search && !loadingS && itemsS.length === 0 ? (
        <NotFound />
      ) : null}
      {layout === Layout.Recommendation ? (
        <FlatList
          showsVerticalScrollIndicator={false}
          data={itemsR}
          ListHeaderComponent={
            <SectionHeader text={LocalText.screens.home.recommendations} />
          }
          ListFooterComponent={() => {
            return itemsR.length > 0 &&
              totalCountR &&
              itemsR.length < totalCountR ? (
              <View style={{ padding: 16 }}>
                <ActivityIndicator color={colors.chokuRed} />
              </View>
            ) : null;
          }}
          renderItem={(element) => (
            <OfferListItem
              key={element.index}
              offer={element.item}
              onPress={() => offerHandler(element.item.id)}
            />
          )}
          onEndReachedThreshold={0.9}
          onEndReached={() => nextPageR()}
        />
      ) : null}
      {layout === Layout.Search && itemsS.length > 0 ? (
        <FlatList
          showsVerticalScrollIndicator={false}
          data={itemsS}
          ListHeaderComponent={
            <SectionHeader text={LocalText.screens.home.searchResults} />
          }
          ListFooterComponent={() => {
            return itemsS.length > 0 &&
              totalCountS &&
              itemsS.length < totalCountS ? (
              <View style={{ padding: 16 }}>
                <ActivityIndicator color={colors.chokuRed} />
              </View>
            ) : null;
          }}
          renderItem={(element) => (
            <OfferListItemLarge
              key={element.index}
              userName={element.item.owner?.name}
              userImageSource={
                element.item.owner?.image
                  ? { uri: element.item.owner?.image }
                  : undefined
              }
              offerName={element.item.title}
              offerImageSource={
                element.item.mainImageUrl
                  ? { uri: element.item.mainImageUrl }
                  : undefined
              }
              categoryName={element.item.category?.title || "unknown"}
              price={element.item.price || 0}
              status={element.item.owner?.status}
              likes={element.item.likeCount}
              isVerified={
                element.item.owner?.identificationStatus === "APPROVED"
              }
              rate={element.item.rate}
              rateCount={element.item.rateCount}
              onPress={() => offerHandler(element.item.id)}
            />
          )}
          onEndReachedThreshold={0.9}
          onEndReached={() => nextPageS()}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  notFound: {
    backgroundColor: colors.gray1,
  },
  headerButtonLeft: {
    marginLeft: 16,
  },
  headerButtonRight: {
    marginRight: 16,
  },
});
